import { IndustryModeEnum } from "../../../api/constants";
import {
  RBPayBusinessType,
  RBPayEmploymentType,
  RBPayPaymentMethod,
  RBPayTaxProfile,
} from "../types/rbPaySettings.types";

export const RB_PAY_ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

export const RB_PAY_MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

// Palette for the redesigned RB Pay setup screen (matches approved design).
export const RB_PAY_SETUP_COLORS = {
  pageBg: "#F3F1F9",
  cardBg: "#FFFFFF",
  cardBorder: "#E5E0F0",
  innerDivider: "#EEE8F8",
  deepPurple: "#2D1B69",
  deepPurpleHover: "#3D2B7A",
  primaryPurple: "#4527A0",
  selectedBg: "#EEE8FC",
  optionOffBorder: "#D5CEEA",
  optionOffBg: "#FAFAFE",
  radioOffBorder: "#C8C0DC",
  textDark: "#1A1035",
  textBody: "#3D3060",
  textMuted: "#A89EC0",
  textLabel: "#7B6FA0",
  textFaint: "#B2A8CC",
  summaryBg: "#F8F6FC",
  summaryBorder: "#EDE8F6",
  noteBg: "#F8F6FC",
  noteBorder: "#EBE6F6",
  bannerFrom: "#2C1A68",
  bannerTo: "#4830A4",
  successText: "#065F46",
  successBg: "#EDFAF4",
  successBorder: "#A7E3C5",
  successIcon: "#059669",
  warnText: "#8C4800",
  warnBg: "#FFF6EE",
  warnBorder: "#F5D4A8",
  warnIcon: "#C8650A",
  stepInactiveBg: "#E4DEEF",
  stepInactiveText: "#7B6FA0",
  editingBadgeBg: "#EEE8FC",
  errorText: "#D32F2F",
  errorBg: "#FDECEC",
  errorBorder: "#F4B8B8",
} as const;
export const RB_PAY_EMAIL_CODE_LENGTH = 6;
export const RB_PAY_SECTION_SPACING = 40;
export const RB_PAY_FIELD_GROUP_SPACING = 24;
export const RB_PAY_LABEL_TO_FIELD_SPACING = 8;
export const RB_PAY_FIELD_TO_VALIDATION_SPACING = 4;
export const RB_PAY_VALIDATION_TO_NEXT_FIELD_SPACING = 12;
export const RB_PAY_BUTTON_GROUP_SPACING = 16;
export const RB_PAY_CARD_PADDING = 24;
export const RB_PAY_CARD_PADDING_MOBILE = 16;

export const RB_PAY_STATEMENT_ACCESS_NOTE =
  "Opens in new window. Use your RB Sign in email to access.";

export const RB_PAY_SETUP_COMPLETE_NOTE =
  "Your RB Pay setup is complete. You can edit your details anytime — updates are resubmitted for review and may need re-approval before taking effect.";

// State-aware progress notices. The checklist notice changes with where the user
// is in the flow rather than just "all steps filled" (isSetupComplete):
//  - incomplete: first-time setup, before Save Setup is clicked
//  - editing:    editing an existing, already-submitted setup
//  - submitted:  saved and awaiting review (status "pending")
//  - complete:   approved / active (RB_PAY_SETUP_COMPLETE_NOTE)
export const RB_PAY_SETUP_NOTES = {
  incomplete: "Your setup is not complete until you click Save Setup at the bottom of this page.",
  editing:
    "You're editing your RB Pay details — click Save Setup to resubmit. Changes go back for review and may need re-approval before taking effect.",
  submitted: "Your RB Pay setup has been submitted and is pending review.",
  onHold: "Your RB Pay is currently on hold. Please review your details or contact support to have it reactivated.",
  complete: RB_PAY_SETUP_COMPLETE_NOTE,
};

export const RB_PAY_FIRST_SUBMIT_TITLE = "RB Pay Setup Submitted";
export const RB_PAY_FIRST_SUBMIT_MESSAGE =
  "Your RB Pay setup has been submitted and is now pending review.\n\n" +
  "Our team will review your information before your account is activated — payments won't begin until your setup is approved. We'll email you once your RB Pay account is approved.";

export const RB_PAY_SUBMIT_ERROR_TITLE = "Submission Failed";
export const RB_PAY_SUBMIT_ERROR_MESSAGE =
  "We couldn't submit your RB Pay information right now. Please check your connection and try again.\n\n" +
  "If the problem continues, please contact support.";

export const RB_PAY_NO_CHANGES_TOOLTIP = "No changes to submit.";

export const RB_PAY_EDIT_SUBMIT_TITLE = "Changes Submitted";
export const RB_PAY_EDIT_SUBMIT_MESSAGE =
  "Your updated RB Pay information has been submitted.\n\n" +
  "Because these changes affect how you're paid, your details will be reviewed again before they take effect — your RB Pay status may return to pending until re-approved. We'll notify you once the review is complete.";

export const RB_PAY_DENTAL_DESCRIPTION =
  "This is RB Pay, allowing you to receive faster payment for your completed shifts. We pay directly to your bank account, taking all appropriate payroll deductions for the profile you select.";

export const RB_PAY_PHARMA_DESCRIPTION =
  "This is RB Pay, allowing you to receive faster payment for your completed shifts.";

export const RB_PAY_TAX_DESCRIPTION =
  "All deductions follow CRA rules. Final taxes are determined when you file your return.";

export const RB_PAY_EMPLOYER_NOTE =
  "Relief Buddy facilitates payments and does not act as an employer.";

export const RB_PAY_BUSINESS_DESCRIPTION =
  "Relief Buddy pays service providers as independent businesses. You can operate as a sole proprietor or corporation.";

export const RB_PAY_BUSINESS_CERTIFICATION_TEXT =
  "I certify that I am operating as an independent contractor/business and not as an employee. I am responsible for my own taxes and obligations.";

export const RB_PAY_DIRECT_DEPOSIT_AUTHORIZATION_TEXT =
  "I authorize Relief Buddy Inc. via RB Pay to deposit payments into my bank account for amounts owed to me in connection with my use of the platform. I confirm that the banking information provided is accurate and belongs to me. I understand that deposits will be processed in accordance with applicable payment rules and may vary based on payment timing and account activity. I acknowledge that I may update or revoke this authorization at any time with reasonable notice, which may affect future payments.";

export const RB_PAY_EMAIL_CERTIFICATION_TEXT =
  "I certify that the email provided is setup for Auto Deposit. I understand that my payments may be delayed if I do not have auto deposits setup.";

export const RB_PAY_BANK_DOCUMENT_HELPER_TEXT =
  "Accepted: Void cheque, or bank-issued account details (PDF or screenshot)";

export const RB_PAY_PHARMA_SIN_HELPER_TEXT =
  "*If you want to change above information, please contact {contactEmail}";

export const RB_PAY_CONTACT_PREFIX = "Questions? Contact us";

export const RB_PAY_PAYMENT_METHOD_OPTIONS = [
  {
    id: RBPayPaymentMethod.DIRECT_DEPOSIT,
    title: "Direct Deposit Information – To receive Payments",
  },
  {
    id: RBPayPaymentMethod.EMAIL_TRANSFER,
    title: "Email Money Transfer – To receive Payments",
  },
];

export const RB_PAY_PROFILE_TYPE_OPTIONS = [
  { id: RBPayEmploymentType.EMPLOYEE, title: "Employee" },
  { id: RBPayEmploymentType.BUSINESS, title: "Business" },
];

export const RB_PAY_TAX_PROFILE_OPTIONS = [
  {
    id: RBPayTaxProfile.HIGH,
    title: "High Tax Profile",
    description:
      "Higher deductions based on estimated annual income to reduce taxes owing at year-end.",
  },
  {
    id: RBPayTaxProfile.LOW,
    title: "Low Tax Profile",
    description:
      "Minimum CRA-required deductions for higher take-home pay now; you may owe taxes later.",
  },
];

export const RB_PAY_BUSINESS_TYPE_OPTIONS = [
  { id: RBPayBusinessType.SOLE_PROPRIETOR, title: "Sole Proprietor" },
  { id: RBPayBusinessType.INCORPORATED, title: "Incorporated" },
];

export const RB_PAY_INDUSTRY_CONFIG = {
  [IndustryModeEnum.Dentistry]: {
    supportsProfileTypeSelection: true,
    showPharmaSinSummary: false,
    description: RB_PAY_DENTAL_DESCRIPTION,
  },
  [IndustryModeEnum.Pharmaceutical]: {
    supportsProfileTypeSelection: false,
    showPharmaSinSummary: true,
    description: RB_PAY_PHARMA_DESCRIPTION,
  },
};
